/**
 * Standard pricing structure for V5000 solutions
 * This defines the shape of all pricing data throughout the application
 * 
 * 
 * 
 * NOTE : VduraCare SSD total is going to be off by 1 unit. This is because of rounding issues. The total does not sum the other SSD parts, it sums all SSD capacity and 
 *        then calculates the price. This causes the total to be off by 1 unit due to math.ceiling issues. We are using the combined pricing function for SSD pricing.
 * 
 */
export const createPricingStructure = () => ({
  software: {
    vduraCare: {
      ssd: {
        directors: 0,
        hybrid: 0,
        afa: 0,
        total: 0
      },
      hdd: {
        hybrid: 0,
        total: 0
      },
      total: 0
    },
    vduraCareSubcomponents: {
      ssd: {
        license: 0,
        support: 0
      },
      hdd: {
        license: 0,
        support: 0
      }
    },
    discounts: {
      promotionalDiscount: 0,
      total: 0
    },
    services: {
      installation: 0,
      total: 0
    },
    total: 0
  },

  hardware: {
    directors: {
      nodes: 0,
      ssds: 0,
      networkCards: 0,
      total: 0
    },
    afa: {
      nodes: 0,
      ssds: 0,
      networkCards: 0,
      total: 0
    },
    hybrid: {
      nodes: 0,
      ssds: 0,
      hdds: 0,
      networkCards: 0,
      total: 0
    },
    total: 0
  },

  solution: {
    softwareTotal: 0,
    hardwareTotal: 0,
    grandTotal: 0
  },

  // Metadata for debugging/validation
  metadata: {
    calculatedAt: null,
    configHash: null, // For detecting when recalculation is needed
    errors: [],
    warnings: []
  },


  // Calculation methods
  calculateSoftwareTotals() {
    // // OFFICIAL CALCUALTION OF SSD SOFTWARE TOTAL - we don't use individual pricing functions here. See comment in calculateSoftwarePricing() in calculatePricingCalculator.jsx
    this.software.vduraCare.ssd.total = this.software.vduraCare.ssd.total;
    // Calculate vduraCare SSD total
    // this.software.vduraCare.ssd.total =
    //   this.software.vduraCare.ssd.directors +
    //   this.software.vduraCare.ssd.hybrid +
    //   this.software.vduraCare.ssd.afa;

    // Calculate vduraCare HDD total
    this.software.vduraCare.hdd.total = this.software.vduraCare.hdd.hybrid;

    // Calculate overall vduraCare total
    this.software.vduraCare.total =
      this.software.vduraCare.ssd.total +
      this.software.vduraCare.hdd.total;

    // Calculate discounts total (in case you add more discount types later)
    this.software.discounts.total = this.software.discounts.promotionalDiscount;

    // Calculate services total
    this.software.services.total = this.software.services.installation;

    // Calculate overall software total
    this.software.total =
      this.software.vduraCare.total +
      this.software.discounts.total +
      this.software.services.total;

    return this;
  },

  calculateHardwareTotals() {
    // Calculate directors total
    this.hardware.directors.total =
      this.hardware.directors.nodes +
      this.hardware.directors.ssds +
      this.hardware.directors.networkCards;

    // Calculate AFA total
    this.hardware.afa.total =
      this.hardware.afa.nodes +
      this.hardware.afa.ssds +
      this.hardware.afa.networkCards;

    // Calculate hybrid total
    this.hardware.hybrid.total =
      this.hardware.hybrid.nodes +
      this.hardware.hybrid.ssds +
      this.hardware.hybrid.hdds +
      this.hardware.hybrid.networkCards;

    // Calculate overall hardware total
    this.hardware.total =
      this.hardware.directors.total +
      this.hardware.afa.total +
      this.hardware.hybrid.total;

    return this;
  },

  calculateSolutionTotals() {
    this.solution.softwareTotal = this.software.total;
    this.solution.hardwareTotal = this.hardware.total;
    this.solution.grandTotal = this.solution.softwareTotal + this.solution.hardwareTotal;
    return this;
  },

  updateMetadata() {
    this.metadata.calculatedAt = new Date().toISOString();

    // Simple hash of the configuration (you might want a more sophisticated approach)
    this.metadata.configHash = this.generateConfigHash();

    return this;
  },
  generateConfigHash() {
    // Create a simple hash of all the non-calculated values
    const configData = {
      software: {
        vduraCare: {
          ssd: {
            directors: this.software.vduraCare.ssd.directors,
            hybrid: this.software.vduraCare.ssd.hybrid,
            afa: this.software.vduraCare.ssd.afa
          },
          hdd: {
            hybrid: this.software.vduraCare.hdd.hybrid
          }
        },
        vduraCareSubcomponents: {
          ssd: {
            license: this.software.vduraCareSubcomponents.ssd.license,
            support: this.software.vduraCareSubcomponents.ssd.support
          },
          hdd: {
            license: this.software.vduraCareSubcomponents.hdd.license,
            support: this.software.vduraCareSubcomponents.hdd.support
          }
        },
        discounts: {
          promotionalDiscount: this.software.discounts.promotionalDiscount
        },
        services: {
          installation: this.software.services.installation
        }
      },
      hardware: {
        directors: {
          nodes: this.hardware.directors.nodes,
          ssds: this.hardware.directors.ssds,
          networkCards: this.hardware.directors.networkCards
        },
        afa: {
          nodes: this.hardware.afa.nodes,
          ssds: this.hardware.afa.ssds,
          networkCards: this.hardware.afa.networkCards
        },
        hybrid: {
          nodes: this.hardware.hybrid.nodes,
          ssds: this.hardware.hybrid.ssds,
          hdds: this.hardware.hybrid.hdds,
          networkCards: this.hardware.hybrid.networkCards
        }
      }
    };

    return JSON.stringify(configData).length.toString(36); // Simple hash
  },

  // Main calculation method that runs all calculations
  calculate() {
    this.calculateSoftwareTotals();
    this.calculateHardwareTotals();
    this.calculateSolutionTotals();
    this.updateMetadata();
    return this;
  },

  // Validation method
  validate() {
    this.metadata.errors = [];
    this.metadata.warnings = [];

    // Check for negative values
    this.validateNonNegativeValues('software.vduraCare.ssd.directors', this.software.vduraCare.ssd.directors);
    this.validateNonNegativeValues('software.vduraCare.ssd.hybrid', this.software.vduraCare.ssd.hybrid);
    this.validateNonNegativeValues('software.vduraCare.ssd.afa', this.software.vduraCare.ssd.afa);
    this.validateNonNegativeValues('software.vduraCare.hdd.hybrid', this.software.vduraCare.hdd.hybrid);
    // This value actually can be negative, since that's how we are displaying the discount total
    // this.validateNonNegativeValues('software.discounts.promotionalDiscount', this.software.discounts.promotionalDiscount);
    this.validateNonNegativeValues('software.services.installation', this.software.services.installation);

    // Hardware validation
    Object.keys(this.hardware).forEach(category => {
      if (category === 'total') return;

      Object.keys(this.hardware[category]).forEach(component => {
        if (component === 'total') return;

        const path = `hardware.${category}.${component}`;
        this.validateNonNegativeValues(path, this.hardware[category][component]);
      });
    });

    // Business logic validations
    if (this.software.discounts.promotionalDiscount > this.software.total) {
      this.metadata.warnings.push('Promotional discount exceeds software total');
    }

    if (this.solution.grandTotal === 0) {
      this.metadata.warnings.push('Grand total is zero - verify configuration');
    }

    return this.metadata.errors.length === 0;
  },

  validateNonNegativeValues(path, value) {
    if (typeof value !== 'number') {
      this.metadata.errors.push(`${path} must be a number, got ${typeof value}`);
    } else if (value < 0) {
      this.metadata.errors.push(`${path} cannot be negative: ${value}`);
    }
  },

  // Helper method to get a summary
  getSummary() {
    return {
      softwareTotal: this.solution.softwareTotal,
      hardwareTotal: this.solution.hardwareTotal,
      grandTotal: this.solution.grandTotal,
      isValid: this.metadata.errors.length === 0,
      hasWarnings: this.metadata.warnings.length > 0,
      lastCalculated: this.metadata.calculatedAt
    };
  }

});

/**
 * OLD CODE BELOW - MAY BE USEFUL LATER
 * Helper function to validate pricing structure
 */
export const validatePricingStructure = (pricing) => {
  const errors = [];

  // Check if software totals add up
  const softwareSum = pricing.software.vduraCare.total +
    pricing.software.discounts.total +
    pricing.software.services.total;
  if (Math.abs(softwareSum - pricing.software.total) > 0.01) {
    errors.push('Software totals do not match');
  }

  // Check if hardware totals add up
  const hardwareSum = pricing.hardware.directors.total +
    pricing.hardware.afa.total +
    pricing.hardware.hybrid.total;
  if (Math.abs(hardwareSum - pricing.hardware.total) > 0.01) {
    errors.push('Hardware totals do not match');
  }

  // Check if solution total matches
  const solutionSum = pricing.software.total + pricing.hardware.total;
  if (Math.abs(solutionSum - pricing.solution.grandTotal) > 0.01) {
    errors.push('Solution total does not match component totals');
  }

  return errors;
};

/**
 * OLD CODE BELOW - MAY BE USEFUL LATER
 * Helper to create a config hash for change detection
 */
export const createConfigHash = (config) => {
  const relevantConfig = {
    quarter: config.quarter,
    subscriptionMonths: config.subscriptionMonths,
    serviceOption: config.serviceOption,
    hybridEnabled: config.hybridEnabled,
    afaEnabled: config.afaEnabled,
    hybridNodeCount: config.hybridNodeCount,
    afaNodeCount: config.afaNodeCount,
    veloCount: config.veloCount,
    ssdSoftware: config.ssdSoftware,
    hddSoftware: config.hddSoftware,
    discountMonths: config.discountMonths
    // Add other pricing-relevant config fields
  };

  return JSON.stringify(relevantConfig).split('').reduce((a, b) => {
    a = ((a << 5) - a) + b.charCodeAt(0);
    return a & a;
  }, 0);
};

/**
 * Cost-efficiency figures derived from a solution grand total.
 *
 * Extracted from useConfigurationLogic so the override-free baseline pass
 * (src/utils/baselinePricing.js) derives them with identical arithmetic
 * instead of a hand-copied second formula. The two sets are rendered side by
 * side on the Special Pricing card, so any drift between them would read as a
 * pricing bug rather than as the code duplication it actually is.
 *
 * Divides by zero on an empty config, yielding Infinity. That is the
 * pre-existing behaviour of the expression this replaced; the card's renderer
 * guards the display rather than the arithmetic being changed here.
 */
export const deriveCostEfficiency = (grandTotal, metrics, compressionRatio) => ({
  dollarsPerRawTB: grandTotal / metrics.totalRawCapacity,
  dollarsPerEffectiveTB: grandTotal / metrics.totalEffectiveCapacity,
  dollarsPerCompressedEffectiveTB: grandTotal / (metrics.totalEffectiveCapacity * compressionRatio),
});